import {createSelector} from 'reselect';
import {mapToColorRecord, sortColors} from '~/utils/catalog';
import {GILDAN_SOFTSTYLE} from '~/constants/ProductConstants';
import {Map} from 'immutable';

export const getStyles = state => state.catalog.get('styles');

export const getCatalogCID = state => state.catalog.get('catalogCID');

export const getCatalogCIDProductColorId = state =>
  state.catalog.get('catalogCIDProductColorId');

export const getErrorFreeStyles = createSelector([getStyles], styles => {
  if (!styles) {
    return Map();
  }

  return styles.filter(
    style =>
      style && !style.isFetching && (!style.errors || !style.errors.length)
  );
});

export const getErrorFreeStylesJs = createSelector(
  [getErrorFreeStyles],
  styles => styles.toJS()
);

export const getComplements = state => state.catalog.get('complements');

export const getIsFetchingComplements = state =>
  state.catalog.get('fetchingComplements');

export const getAlternatives = state => state.catalog.get('alternatives');

export const getCompatibleStyles = state =>
  state.catalog.get('compatibleStyles');

export const getStylesArray = createSelector([getStyles], styles =>
  Array.from(styles.values())
);

export const getStyle = (state, props) => {
  if (!props?.styleId) return undefined;
  return getStyles(state)?.get(props.styleId);
};

export const getSizesWithStatusForStyleAndColorIds =
  state => (styleId, colorId) => {
    const style = Array.from(state.catalog.get('styles').values()).filter(
      style => style.id === styleId
    ); // can use here
    const color = style[0].colors.filter(color => color.id === colorId)[0];
    return color && color.sizesWithStatus;
  };

export const isDefaultStyleLoaded = state => {
  const styles = Array.from(state.catalog.get('styles').values()).map(
    style => style.id
  );
  return styles.indexOf(getDefaultStyleId(state)) > -1;
};

export const getDefaultStyleId = () => {
  return GILDAN_SOFTSTYLE.styleId;
};

export const getColor = (state, props) => {
  if (!props?.styleId || !props?.colorId) return undefined;
  const style = getStyle(state, props);
  if (!style) return undefined;

  // In the test, the colors are directly in the style object
  if (style.colors && Array.isArray(style.colors)) {
    return style.colors.find(color => color.id === props.colorId);
  }

  return undefined;
};

export const getSizesForColor = (state, props) => {
  const color = getColor(state, props);
  return color && color.sizes ? color.sizes : [];
};

export const getSortedProductColors = createSelector([getStyle], sortColors);

export const getProductColors = createSelector(
  [getSortedProductColors],
  mapToColorRecord
);

export const getActiveProductColors = createSelector(
  [getSortedProductColors],
  sortedColors => {
    return mapToColorRecord(
      sortedColors.filter(color => color.status === 'active')
    );
  }
);

export const getActiveSinglesProductColors = createSelector(
  [getSortedProductColors],
  sortedColors =>
    mapToColorRecord(
      sortedColors.filter(
        color => color.status === 'active' && color.singles_enabled
      )
    )
);

export const getActiveBulkProductColors = createSelector(
  [getSortedProductColors],
  sortedColors =>
    mapToColorRecord(
      sortedColors.filter(
        color => color.status === 'active' && !color.singles_enabled
      )
    )
);

export const areComplementProducts =
  state =>
  ({previousProduct, nextProduct}) => {
    const complementStyles = getComplements(state)?.toJS?.();

    const previousProductComplements = complementStyles
      ? complementStyles[previousProduct?.styleId]
      : null;

    return !!previousProductComplements?.find(
      styleId => Number(styleId) === Number(nextProduct?.styleId)
    );
  };

export const areCompatibleProducts =
  state =>
  ({previousProduct, nextProduct}) => {
    const compatibleStyles = getCompatibleStyles(state)?.toJS?.();

    const previousProductCompatibles = compatibleStyles
      ? compatibleStyles[previousProduct?.styleId]
      : null;

    return !!previousProductCompatibles?.find(
      styleId => Number(styleId) === Number(nextProduct?.styleId)
    );
  };

export const isFetchingStyleComplements = (state, props) => {
  const complements = getIsFetchingComplements(state);
  const styleComplementsStatus = complements?.get(props?.styleId);
  return !!styleComplementsStatus?.isFetching;
};
